import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { BulletsEditor } from './BulletsEditor';
import { ChampEtiquete } from './ChampEtiquete';
import { SectionRepliable } from './SectionRepliable';
import { TagEditor } from './TagEditor';
import { QuantifyHint } from './QuantifyHint';
import { Plus, Sparkles, X } from './icons';
import { colors, formes, spacing, typography } from '../theme/tokens';
import type { CvEducation, CvExperience, CvProject, GeneratedCv } from '../types';
import { t } from '../i18n';
import { TEXTE_LTR } from '../i18n/sens';

// Résultat d'une demande de réécriture IA (gérée hors composant : screen → service).
export type RewriteOutcome = { ok: true; after: string } | { ok: false; quota?: boolean; message: string };
export type OnRewrite = (section: string, content: string) => Promise<RewriteOutcome>;

interface CvEditorProps {
  value: GeneratedCv;
  onChange: (cv: GeneratedCv) => void;
  onRewrite?: OnRewrite; // si fourni : bouton « Améliorer avec l'IA » par expérience/projet
  testID?: string;
}

// Découpe la proposition IA (texte) en bullets : une ligne = un bullet, puces/numéros retirés.
export function splitBullets(after: string): string[] {
  return after
    .split('\n')
    .map((line) => line.replace(/^\s*([-•*–·]|\d+[.)])\s*/, '').trim())
    .filter(Boolean);
}

type RwStatus = 'idle' | 'loading' | 'proposed' | 'quota' | 'error';

// Réécriture IA d'une entrée, avec AVANT/APRÈS : n'écrase jamais sans acceptation.
function RewriteControls({
  section,
  bullets,
  onRewrite,
  onAccept,
  testIDPrefix,
}: {
  section: string;
  bullets: string[];
  onRewrite: OnRewrite;
  onAccept: (bullets: string[]) => void;
  testIDPrefix: string;
}) {
  const [status, setStatus] = useState<RwStatus>('idle');
  const [after, setAfter] = useState('');
  const [message, setMessage] = useState('');
  const nothingToImprove = bullets.every((b) => !b.trim());

  const run = async () => {
    setStatus('loading');
    setMessage('');
    const outcome = await onRewrite(section, bullets.filter((b) => b.trim()).join('\n'));
    if (outcome.ok) {
      setAfter(outcome.after);
      setStatus('proposed');
    } else if (outcome.quota) {
      setMessage(outcome.message);
      setStatus('quota');
    } else {
      setMessage(outcome.message);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return <ActivityIndicator testID={`${testIDPrefix}-rw-loading`} color={colors.violetProfil} />;
  }

  if (status === 'proposed') {
    const proposed = splitBullets(after);
    return (
      <View testID={`${testIDPrefix}-rw-proposal`} style={styles.rwProposal}>
        <Text style={styles.rwLabel}>{t('cvEditor.before')}</Text>
        {bullets.filter((b) => b.trim()).map((b, i) => (
          <Text key={i} style={styles.rwBefore}>• {b}</Text>
        ))}
        <Text style={[styles.rwLabel, styles.rwLabelApres]}>{t('cvEditor.after')}</Text>
        {proposed.map((b, i) => (
          <Text key={i} style={styles.rwAfter}>• {b}</Text>
        ))}
        <View style={styles.rwActions}>
          <Pressable
            testID={`${testIDPrefix}-rw-refuse`}
            accessibilityRole="button"
            onPress={() => setStatus('idle')}
            style={[styles.rwBouton, styles.rwRefuser]}
          >
            <Text style={styles.rwRefuserTexte}>{t('cvEditor.reject')}</Text>
          </Pressable>
          <Pressable
            testID={`${testIDPrefix}-rw-accept`}
            accessibilityRole="button"
            onPress={() => {
              onAccept(proposed);
              setStatus('idle');
            }}
            style={[styles.rwBouton, styles.rwAccepter]}
          >
            <Text style={styles.rwAccepterTexte}>{t('cvEditor.accept')}</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.rwRow}>
      <Pressable
        testID={`${testIDPrefix}-improve`}
        accessibilityRole="button"
        accessibilityLabel={`${t('cvEditor.improveWithAi')} - ${section}`}
        accessibilityState={{ disabled: nothingToImprove }}
        disabled={nothingToImprove}
        onPress={run}
        style={[styles.rwAmeliorer, nothingToImprove && styles.rwAmeliorerInactif]}
      >
        <Sparkles size={15} color={colors.violetProfil} />
        <Text style={styles.rwAmeliorerTexte}>{t('cvEditor.improveWithAi')}</Text>
      </Pressable>
      {status === 'quota' ? (
        <Text testID={`${testIDPrefix}-rw-quota`} style={styles.rwQuota}>
          {message}
        </Text>
      ) : null}
      {status === 'error' ? (
        <Text testID={`${testIDPrefix}-rw-error`} style={styles.rwError}>
          {message} {t('cvEditor.rewriteRetry')}
        </Text>
      ) : null}
    </View>
  );
}

const EMPTY_EXP: CvExperience = { role: '', company: '', start_date: '', end_date: '', bullets: [] };
const EMPTY_PROJ: CvProject = { name: '', date: '', bullets: [] };
const EMPTY_EDU: CvEducation = { degree: '', school: '', date: '' };

type SectionCle = 'experiences' | 'projects' | 'education' | 'skills' | 'languages' | 'interests';

// À l'ouverture de l'éditeur : les expériences d'abord, le reste d'un toucher.
const OUVERTES_AU_DEPART: Record<SectionCle, boolean> = {
  experiences: true,
  projects: false,
  education: false,
  skills: false,
  languages: false,
  interests: false,
};

function EnteteEntree({
  titre,
  retirerLabel,
  onRetirer,
  testID,
}: {
  titre: string;
  retirerLabel: string;
  onRetirer: () => void;
  testID: string;
}) {
  return (
    <View style={styles.entryHead}>
      <Text style={styles.entryTitle}>{titre}</Text>
      <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={retirerLabel}
        onPress={onRetirer}
        style={styles.remove}
      >
        <X size={18} color={colors.grey500} />
      </Pressable>
    </View>
  );
}

function BoutonAjouter({ label, onPress, testID }: { label: string; onPress: () => void; testID: string }) {
  return (
    <Pressable testID={testID} accessibilityRole="button" onPress={onPress} style={styles.ajouter}>
      <Plus size={15} color={colors.electricDeep} strokeWidth={3} />
      <Text style={styles.ajouterTexte}>{label}</Text>
    </Pressable>
  );
}

export function CvEditor({ value, onChange, onRewrite, testID }: CvEditorProps) {
  const patch = (p: Partial<GeneratedCv>) => onChange({ ...value, ...p });
  const [ouvertes, setOuvertes] = useState(OUVERTES_AU_DEPART);
  const basculer = (cle: SectionCle) => setOuvertes((o) => ({ ...o, [cle]: !o[cle] }));
  // Rang de l'entrée qui vient d'être ajoutée, par section : son premier champ prend le focus.
  const nouvelle = useRef({ experiences: -1, projects: -1, education: -1 });
  // Une seule fois : sans cela, une entrée remontée plus tard à ce rang reprendrait le focus.
  useEffect(() => {
    nouvelle.current = { experiences: -1, projects: -1, education: -1 };
  });

  const updateExp = (i: number, p: Partial<CvExperience>) =>
    patch({ experiences: value.experiences.map((e, idx) => (idx === i ? { ...e, ...p } : e)) });
  const updateProj = (i: number, p: Partial<CvProject>) =>
    patch({ projects: value.projects.map((e, idx) => (idx === i ? { ...e, ...p } : e)) });
  const updateEdu = (i: number, p: Partial<CvEducation>) =>
    patch({ education: value.education.map((e, idx) => (idx === i ? { ...e, ...p } : e)) });

  return (
    <View testID={testID} style={styles.container}>
      <ChampEtiquete
        ltr
        testID="cve-headline"
        label={t('cvEditor.headline')}
        placeholder={t('cvEditor.headlinePlaceholder')}
        value={value.headline}
        onChangeText={(headline) => patch({ headline })}
      />

      <SectionRepliable
        testID="cve-sec-experiences"
        garderMonte
        titre={t('cvEditor.experiences')}
        compteur={value.experiences.length}
        ouverte={ouvertes.experiences}
        onToggle={() => basculer('experiences')}
      >
        {value.experiences.map((exp, i) => (
          <View key={i} testID={`cve-exp-${i}`} style={styles.entry}>
            <EnteteEntree
              titre={t('editors.experience.rowTitle', { n: i + 1 })}
              retirerLabel={`${t('a11y.removeExperience')} ${i + 1}`}
              testID={`cve-exp-remove-${i}`}
              onRetirer={() => patch({ experiences: value.experiences.filter((_, idx) => idx !== i) })}
            />
            <ChampEtiquete
              ltr
              testID={`cve-exp-role-${i}`}
              label={t('editors.experience.roleLabel')}
              autoFocus={i === nouvelle.current.experiences}
              value={exp.role}
              onChangeText={(role) => updateExp(i, { role })}
            />
            <ChampEtiquete
              ltr
              testID={`cve-exp-company-${i}`}
              label={t('editors.experience.company')}
              value={exp.company}
              onChangeText={(company) => updateExp(i, { company })}
            />
            <View style={styles.dates}>
              <View style={styles.moitie}>
                <ChampEtiquete
                  ltr
                  testID={`cve-exp-start-${i}`}
                  label={t('cvEditor.startLabel')}
                  placeholder={t('cvEditor.startPlaceholder')}
                  value={exp.start_date}
                  onChangeText={(start_date) => updateExp(i, { start_date })}
                />
              </View>
              <View style={styles.moitie}>
                <ChampEtiquete
                  ltr
                  testID={`cve-exp-end-${i}`}
                  label={t('cvEditor.endLabel')}
                  placeholder={t('cvEditor.endPlaceholder')}
                  value={exp.end_date}
                  onChangeText={(end_date) => updateExp(i, { end_date })}
                />
              </View>
            </View>
            <BulletsEditor value={exp.bullets} onChange={(bullets) => updateExp(i, { bullets })} testID={`cve-exp-bullets-${i}`} />
            <QuantifyHint />
            {onRewrite ? (
              <RewriteControls
                section={[exp.role, exp.company].filter(Boolean).join(' - ') || t('cvEditor.experienceLabel')}
                bullets={exp.bullets}
                onRewrite={onRewrite}
                onAccept={(bullets) => updateExp(i, { bullets })}
                testIDPrefix={`cve-exp-${i}`}
              />
            ) : null}
          </View>
        ))}
        <BoutonAjouter
          testID="cve-exp-add"
          label={t('editors.experience.add')}
          onPress={() => {
            nouvelle.current.experiences = value.experiences.length;
            patch({ experiences: [...value.experiences, { ...EMPTY_EXP }] });
          }}
        />
      </SectionRepliable>

      <SectionRepliable
        testID="cve-sec-projects"
        garderMonte
        titre={t('cvEditor.projects')}
        compteur={value.projects.length}
        ouverte={ouvertes.projects}
        onToggle={() => basculer('projects')}
      >
        {value.projects.map((proj, i) => (
          <View key={i} testID={`cve-proj-${i}`} style={styles.entry}>
            <EnteteEntree
              titre={t('editors.project.rowTitle', { n: i + 1 })}
              retirerLabel={`${t('a11y.removeProject')} ${i + 1}`}
              testID={`cve-proj-remove-${i}`}
              onRetirer={() => patch({ projects: value.projects.filter((_, idx) => idx !== i) })}
            />
            <ChampEtiquete
              ltr
              testID={`cve-proj-name-${i}`}
              label={t('editors.project.name')}
              autoFocus={i === nouvelle.current.projects}
              value={proj.name}
              onChangeText={(name) => updateProj(i, { name })}
            />
            <ChampEtiquete
              ltr
              testID={`cve-proj-date-${i}`}
              label={t('cvEditor.dateLabel')}
              placeholder={t('cvEditor.projectDatePlaceholder')}
              value={proj.date}
              onChangeText={(date) => updateProj(i, { date })}
            />
            <BulletsEditor value={proj.bullets} onChange={(bullets) => updateProj(i, { bullets })} testID={`cve-proj-bullets-${i}`} />
            <QuantifyHint />
            {onRewrite ? (
              <RewriteControls
                section={proj.name.trim() || t('cvEditor.projectLabel')}
                bullets={proj.bullets}
                onRewrite={onRewrite}
                onAccept={(bullets) => updateProj(i, { bullets })}
                testIDPrefix={`cve-proj-${i}`}
              />
            ) : null}
          </View>
        ))}
        <BoutonAjouter
          testID="cve-proj-add"
          label={t('editors.project.add')}
          onPress={() => {
            nouvelle.current.projects = value.projects.length;
            patch({ projects: [...value.projects, { ...EMPTY_PROJ }] });
          }}
        />
      </SectionRepliable>

      <SectionRepliable
        testID="cve-sec-education"
        titre={t('cvEditor.education')}
        compteur={value.education.length}
        ouverte={ouvertes.education}
        onToggle={() => basculer('education')}
      >
        {value.education.map((edu, i) => (
          <View key={i} testID={`cve-edu-${i}`} style={styles.entry}>
            <EnteteEntree
              titre={t('editors.education.rowTitle', { n: i + 1 })}
              retirerLabel={`${t('a11y.removeEducation')} ${i + 1}`}
              testID={`cve-edu-remove-${i}`}
              onRetirer={() => patch({ education: value.education.filter((_, idx) => idx !== i) })}
            />
            <ChampEtiquete
              ltr
              testID={`cve-edu-degree-${i}`}
              label={t('editors.education.degree')}
              autoFocus={i === nouvelle.current.education}
              value={edu.degree}
              onChangeText={(degree) => updateEdu(i, { degree })}
            />
            <ChampEtiquete
              ltr
              testID={`cve-edu-school-${i}`}
              label={t('editors.education.school')}
              value={edu.school}
              onChangeText={(school) => updateEdu(i, { school })}
            />
            <ChampEtiquete
              ltr
              testID={`cve-edu-date-${i}`}
              label={t('cvEditor.dateLabel')}
              placeholder={t('cvEditor.educationDatePlaceholder')}
              value={edu.date}
              onChangeText={(date) => updateEdu(i, { date })}
            />
          </View>
        ))}
        <BoutonAjouter
          testID="cve-edu-add"
          label={t('editors.education.add')}
          onPress={() => {
            nouvelle.current.education = value.education.length;
            patch({ education: [...value.education, { ...EMPTY_EDU }] });
          }}
        />
      </SectionRepliable>

      <SectionRepliable
        testID="cve-sec-skills"
        titre={t('cvEditor.skills')}
        compteur={value.skills.length}
        ouverte={ouvertes.skills}
        onToggle={() => basculer('skills')}
      >
        <TagEditor
          value={value.skills}
          onChange={(skills) => patch({ skills })}
          placeholder={t('cvEditor.skillPlaceholder')}
          etiquette={t('cvEditor.skills')}
          testID="cve-skills"
        />
      </SectionRepliable>

      <SectionRepliable
        testID="cve-sec-languages"
        titre={t('cvEditor.languages')}
        compteur={value.languages.length}
        ouverte={ouvertes.languages}
        onToggle={() => basculer('languages')}
      >
        <TagEditor
          value={value.languages}
          onChange={(languages) => patch({ languages })}
          placeholder={t('cvEditor.languagePlaceholder')}
          etiquette={t('cvEditor.languages')}
          testID="cve-languages"
        />
      </SectionRepliable>

      <SectionRepliable
        testID="cve-sec-interests"
        titre={t('cvEditor.interests')}
        compteur={value.interests.length}
        ouverte={ouvertes.interests}
        onToggle={() => basculer('interests')}
      >
        <TagEditor
          value={value.interests}
          onChange={(interests) => patch({ interests })}
          placeholder={t('cvEditor.interestPlaceholder')}
          etiquette={t('cvEditor.interests')}
          testID="cve-interests"
        />
      </SectionRepliable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.md },
  entry: {
    gap: spacing.sm,
    borderRadius: formes.rayon.md,
    borderWidth: 1,
    borderColor: colors.bordureClaire,
    padding: spacing.md,
  },
  entryHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  entryTitle: { flex: 1, fontSize: typography.bodyMd.size, fontWeight: '800', color: colors.night },
  remove: { width: formes.cible, height: formes.cible, alignItems: 'center', justifyContent: 'center' },
  dates: { flexDirection: 'row', gap: spacing.sm },
  moitie: { flex: 1, minWidth: 0 },
  ajouter: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs,
    minHeight: formes.cible,
  },
  ajouterTexte: { fontSize: 14, fontWeight: '800', color: colors.electricDeep },
  // ── Réécriture IA : violet du profil (6,4:1 sur blanc) pour le texte, voile violet pour le fond ──
  rwRow: { gap: spacing.xs },
  rwAmeliorer: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs + 2,
    minHeight: formes.cible,
    paddingHorizontal: spacing.lg,
    borderRadius: formes.rayon.xl,
    borderWidth: 1.5,
    borderColor: colors.violetLogo,
    backgroundColor: colors.violetVeil,
  },
  rwAmeliorerInactif: { opacity: 0.45 },
  rwAmeliorerTexte: { fontSize: 14, fontWeight: '800', color: colors.violetProfil },
  rwProposal: {
    gap: spacing.xs,
    backgroundColor: colors.violetVeil,
    borderRadius: formes.rayon.md,
    padding: spacing.md,
  },
  rwLabel: {
    fontSize: typography.caption.size,
    fontWeight: '800',
    letterSpacing: typography.caption.letterSpacing,
    color: colors.grey500,
    marginTop: spacing.xs,
  },
  rwLabelApres: { color: colors.violetProfil },
  // Texte du CV : de gauche à droite même en arabe.
  rwBefore: { ...TEXTE_LTR, fontSize: typography.small.size, color: colors.grey700 },
  rwAfter: { ...TEXTE_LTR, fontSize: typography.body.size, fontWeight: '700', color: colors.night },
  rwActions: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm },
  rwBouton: {
    flex: 1,
    minHeight: formes.cible,
    borderRadius: formes.rayon.xl,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
  },
  rwRefuser: { borderWidth: 1.5, borderColor: colors.violetLogo, backgroundColor: colors.white },
  rwRefuserTexte: { fontSize: 14, fontWeight: '800', color: colors.violetProfil },
  rwAccepter: { backgroundColor: colors.violetProfil },
  rwAccepterTexte: { fontSize: 14, fontWeight: '800', color: colors.white },
  rwQuota: { color: colors.warning, fontSize: typography.small.size, fontWeight: typography.bodyMd.weight },
  rwError: { color: colors.error, fontSize: typography.small.size },
});
